import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Pagination,
  Chip,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ChatIcon from "@mui/icons-material/Chat";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const WhatsappBusinessGroupContactsView = () => {
  const { groupName } = useParams();
  const navigate = useNavigate();
  const decodedGroupName = decodeURIComponent(groupName || "");
  const notify = useNotification();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  });
  const [sortOrder, setSortOrder] = useState("desc");
  const [phoneSearch, setPhoneSearch] = useState("");
   const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const fetchContacts = async (page = 1) => {
    if (!decodedGroupName) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `${base_url}/whatsapp-business/groups/${encodeURIComponent(
          decodedGroupName
        )}/contacts`,
        { params: { per_page: 50, page, sort_order: sortOrder } }
      );
      if (res.data?.success) {
        setContacts(Array.isArray(res.data.data) ? res.data.data : []);
        setPagination(res.data.pagination || pagination);
        setSelectedIds([]);
      } else {
        setContacts([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        setSelectedIds([]);
      }
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to load group contacts",
        "error"
      );
      setContacts([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (decodedGroupName) {
      fetchContacts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedGroupName, sortOrder]);

  const handlePageChange = (_, value) => {
    fetchContacts(value);
  };

  const handleToggleContact = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const hasSelection = selectedIds.length > 0;

  const handleDeleteSelected = async () => {
    if (!hasSelection || !decodedGroupName) return;
    try {
      setDeleting(true);
      const res = await axiosInstance.post(
        `${base_url}/whatsapp-business/groups/delete-selected-contacts`,
        {
          group_name: decodedGroupName,
          contact_ids: selectedIds,
        }
      );
      notify(
        res.data?.message || "Selected contacts deleted successfully.",
        "success"
      );
      await fetchContacts(pagination.current_page || 1);
      setSelectedIds([]);
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to delete selected contacts",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  const filteredContacts = phoneSearch.trim()
    ? contacts.filter((c) =>
        (c.phone_number || "")
          .toLowerCase()
          .includes(phoneSearch.trim().toLowerCase())
      )
    : contacts;

  return (
    <Box sx={WB_SX.page}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Tooltip title="Back to groups">
          <IconButton
            size="small"
            onClick={() => navigate("/whatsapp-business/groups")}
            aria-label="Back to groups"
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6" sx={WB_SX.pageTitle}>
            Group Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Group: <strong>{decodedGroupName}</strong>
          </Typography>
        </Box>
      </Box>

      <Card elevation={0} sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
        <CardContent sx={WB_SX.cardContent}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Contacts
            </Typography>
            <Chip
              size="small"
              label={`Total: ${pagination.total ?? contacts.length ?? 0}`}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              label="Search phone"
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Sort by created</InputLabel>
              <Select
                value={sortOrder}
                label="Sort by created"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="desc">Newest first</MenuItem>
                <MenuItem value="asc">Oldest first</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={!hasSelection || deleting}
              onClick={handleDeleteSelected}
            >
              Delete selected
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredContacts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No contacts found in this group.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ ...WB_SX.tableHeadCell, width: 48 }}
                    >
                      <Checkbox
                        size="small"
                        sx={{ color: "#fff" }}
                        indeterminate={
                          filteredContacts.length > 0 &&
                          filteredContacts.some((c) => selectedIds.includes(c.id)) &&
                          !filteredContacts.every((c) => selectedIds.includes(c.id))
                        }
                        checked={
                          filteredContacts.length > 0 &&
                          filteredContacts.every((c) => selectedIds.includes(c.id))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            const visibleIds = filteredContacts.map((c) => c.id);
                            setSelectedIds((prev) =>
                              Array.from(new Set([...prev, ...visibleIds]))
                            );
                          } else {
                            const visibleSet = new Set(
                              filteredContacts.map((c) => c.id)
                            );
                            setSelectedIds((prev) =>
                              prev.filter((id) => !visibleSet.has(id))
                            );
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={WB_SX.tableHeadCell}
                    >
                      S No
                    </TableCell>
                    <TableCell
                      sx={WB_SX.tableHeadCell}
                    >
                      Phone Number
                    </TableCell>
                    <TableCell
                      sx={WB_SX.tableHeadCell}
                    >
                      Contact Person Name
                    </TableCell>
                    <TableCell
                      sx={WB_SX.tableHeadCell}
                    >
                      Header Parameters
                    </TableCell>
                    <TableCell
                      sx={WB_SX.tableHeadCell}
                    >
                      Body Parameters
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={WB_SX.tableHeadCell}
                    >
                      View Chats
                    </TableCell>
                    {/* <TableCell
                      sx={{ backgroundColor: "#1a2333", color: "#fff", fontWeight: 700 }}
                    >
                      Created At
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#1a2333", color: "#fff", fontWeight: 700 }}
                    >
                      Updated At
                    </TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts.map((c, index) => (
                    <TableRow
                      key={c.id}
                      sx={WB_SX.tableRow}
                    >
                      <TableCell>
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => handleToggleContact(c.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{index + 1}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor:
                                index % 4 === 0
                                  ? "rgba(25,118,210,0.2)"
                                  : index % 4 === 1
                                  ? "rgba(46,125,50,0.2)"
                                  : index % 4 === 2
                                  ? "rgba(156,39,176,0.2)"
                                  : "rgba(255,143,0,0.2)",
                              color:
                                index % 4 === 0
                                  ? "primary.main"
                                  : index % 4 === 1
                                  ? "success.main"
                                  : index % 4 === 2
                                  ? "secondary.main"
                                  : "warning.dark",
                            }}
                          >
                            <PersonIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {c.phone_number}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.25, display: "block" }}
                            >
                              Created:{" "}
                              {c.created_at
                                ? new Date(c.created_at).toLocaleString()
                                : "—"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {c.contact_person_name || "Not available"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {Array.isArray(c.header_param) && c.header_param.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {c.header_param.map((param, idx) => (
                              <Chip
                                key={`header-${c.id}-${idx}`}
                                label={String(param)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {Array.isArray(c.body_param) && c.body_param.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {c.body_param.map((param, idx) => (
                              <Chip
                                key={`body-${c.id}-${idx}`}
                                label={String(param)}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View chats">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              window.open(
                                `/whatsapp-business/chat-window/contact/${encodeURIComponent(
                                  c.phone_number || ""
                                )}`,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      {/* <TableCell>
                        {c.created_at
                          ? new Date(c.created_at).toLocaleString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {c.updated_at
                          ? new Date(c.updated_at).toLocaleString()
                          : "—"}
                      </TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.last_page > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={pagination.last_page}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsappBusinessGroupContactsView;

